import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { PawPrint, Cake, Heart, Sparkles, Camera } from "lucide-react";
import PetsnapLogo from "@/components/PetsnapLogo";
import { SHARE } from "@/constants/testIds";

export default function ShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/pets/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This scrapbook is private or doesn't exist."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-[#F4EFE6] flex items-center justify-center mx-auto mb-4">
            <PawPrint className="w-8 h-8 text-[#E76F51]" />
          </div>
          <h1 className="font-display text-3xl font-bold text-[#2D232E]">{error}</h1>
          <p className="font-hand text-xl text-[#6A606C] mt-2">try a different link maybe?</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center font-hand text-3xl text-[#E76F51]">loading...</div>;

  const { pet, owner_name, photos } = data;
  const photoSrc = (photoId) => `${API}/public/photos/${token}/${photoId}`;

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-grain">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#EAE2D6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PetsnapLogo className="w-8 h-8" />
            <span className="font-display text-xl font-bold text-[#2D232E]">Petsnap</span>
          </div>
          <span className="font-hand text-lg text-[#6A606C]">shared scrapbook</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-10 relative z-10">
        <section data-testid={SHARE.publicHero} className="rounded-3xl bg-white border border-[#EAE2D6] warm-shadow overflow-hidden mb-10 relative">
          <div className="tape-strip" />
          <div className="grid md:grid-cols-3">
            <div className="aspect-square md:aspect-auto relative" style={{ backgroundColor: `${pet.color}22` }}>
              {pet.cover_photo_id ? (
                <img src={photoSrc(pet.cover_photo_id)} alt={pet.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PawPrint className="w-24 h-24" style={{ color: pet.color }} />
                </div>
              )}
            </div>
            <div className="md:col-span-2 p-8">
              <div className="font-hand text-2xl mb-1" style={{ color: pet.color }}>meet</div>
              <h1 className="font-display text-6xl font-bold text-[#2D232E] tracking-tight leading-none mb-4">{pet.name}</h1>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm font-semibold px-3 py-1 rounded-full capitalize" style={{ backgroundColor: `${pet.color}22`, color: pet.color }}>
                  {pet.species}
                </span>
                {pet.breed && <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#F4EFE6] text-[#6A606C]">{pet.breed}</span>}
                {pet.birthday && (
                  <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#F4EFE6] text-[#6A606C] inline-flex items-center gap-1">
                    <Cake className="w-3.5 h-3.5" /> {new Date(pet.birthday).toLocaleDateString()}
                  </span>
                )}
                <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[#F4EFE6] text-[#6A606C] inline-flex items-center gap-1">
                  <Camera className="w-3.5 h-3.5" /> {photos.length} photos
                </span>
              </div>
              {pet.notes && <p className="font-hand text-2xl text-[#6A606C] mb-4">{pet.notes}</p>}
              <p className="text-sm text-[#6A606C]">
                Curated with <Heart className="w-3.5 h-3.5 inline fill-[#E76F51] text-[#E76F51]" /> by <span className="font-semibold text-[#2D232E]">{owner_name}</span>
              </p>
            </div>
          </div>
        </section>

        {photos.length === 0 ? (
          <div className="text-center text-[#6A606C] font-hand text-2xl">no photos yet</div>
        ) : (
          <div className="columns-2 md:columns-3 gap-6">
            {photos.map((p, i) => (
              <div
                key={p.id}
                onClick={() => setLightbox(p)}
                className={`polaroid cursor-pointer break-inside-avoid mb-6 ${i % 5 === 0 ? "-rotate-1" : i % 3 === 0 ? "rotate-1" : "rotate-0"} hover:z-10 hover:!rotate-0 transition-transform`}
              >
                <div className="relative overflow-hidden rounded-sm bg-[#F4EFE6]">
                  <img src={photoSrc(p.id)} alt={p.caption || ""} className="w-full h-auto block" loading="lazy" />
                  {p.ai_status === "done" && p.ai_breed && (
                    <div className="absolute bottom-2 left-2 bg-[#2D232E]/80 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {p.ai_breed}
                    </div>
                  )}
                </div>
                <div className="pt-3 px-1 font-hand text-xl text-[#2D232E] leading-tight truncate">
                  {p.caption || "untitled"}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-pointer"
        >
          <img src={photoSrc(lightbox.id)} alt={lightbox.caption || ""} className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}

      <footer className="text-center py-8 text-sm text-[#6A606C]">
        Made with <Heart className="w-3.5 h-3.5 inline fill-[#E76F51] text-[#E76F51]" /> on Petsnap
      </footer>
    </div>
  );
}
